import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
} from "shared/ui/shadcn/select";

import { FC, useMemo } from "react";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { clsxMerge } from "shared/lib/helpers";
import { useEmailTemplatesContext } from "@/components/modules/email-templates-sidebar/context";

import { LayoutPanelTopIcon, PlusIcon, XIcon } from "lucide-react";
import { useHandlerDeletePrompt } from "@/components/modules/email-templates-sidebar/hooks";
import { useQueryCreateEmailTemplate } from "@/components/modules/email-templates-sidebar/queries";
import { DEFAULT_PROMPT_NAME } from "@/components/modules/email-templates-sidebar/constants";

const ADD_PROMPT_SELECT_ITEM_KEY = "add-prompt-select-item";

export const TemplatesSelect: FC<PropsWithClassNameI> = ({ className }) => {
  const {
    templates: templatesData,
    activeTemplate,
    setActiveTemplateId,
    isAIGenerationStarted: isLoading,
    setEditMode,
  } = useEmailTemplatesContext();

  const { mutateAsync: createTemplate } = useQueryCreateEmailTemplate();
  const { openModal: openDeletePromptModal } = useHandlerDeletePrompt();

  const templates = useMemo(
    () => templatesData?.data?.email_templates,
    [templatesData?.data?.email_templates]
  );

  const handleSelectTemplate = async (templateId: string) => {
    if (ADD_PROMPT_SELECT_ITEM_KEY === templateId) {
      //TODO Create tempalte and set active template as created one
      const resp = await createTemplate({
        name: DEFAULT_PROMPT_NAME,
        prompt: "",
      });

      if (resp?.status !== 200) {
        return;
      }

      setActiveTemplateId(resp?.data?.email_template?.id);
      return;
    }

    if (templates?.find((template) => template?.id === templateId)?.is_custom) {
      return;
    }

    setEditMode(false);
    setActiveTemplateId(templateId);
  };

  const defaultEmailTemplates = useMemo(
    () => templates?.filter((template) => !template.is_custom),
    [templates]
  );

  const customEmailTemplates = useMemo(
    () => templates?.filter((template) => template.is_custom),
    [templates]
  );

  /**
   * Due to  a known and unavoidable limitation of the Radix UI SelectItem component:
   * Radix SelectItem cannot prevent selection via event.stopPropagation() or event.preventDefault() on inner elements.
   *
   * This is because SelectItem:
   *
   * Internally listens to onPointerDown (not onClick) at the item level.
   *
   * Ignores bubbling events and directly processes selection from the root node or nearest data-radix-select-item.
   *
   * So unfortunately, no combination of stopPropagation, preventDefault, or even capturing onMouseDown at parent level will stop it from closing the dropdown and selecting the item.
   */
  const handleSelectCustomTemplate = (templateId?: string) => (e: any) => {
    e.stopPropagation();
    e.preventDefault();

    const actionType = e.target
      .closest("[data-custom-template-action-type]")
      ?.getAttribute("data-custom-template-action-type");

    if (actionType === "delete") {
      setActiveTemplateId(templateId);

      /**
       * On selecting template to delete, Select component will unmount
       * so we need to open the modal with a timeout when modal is ready in the edit mode
       */
      setTimeout(() => {
        openDeletePromptModal();
      }, 100);
      return;
    }

    setEditMode(false);
    setActiveTemplateId(templateId);
  };

  return (
    <div className={clsxMerge(className)}>
      <h6 className="mb-3 typography-body-4-medium">Choose an email prompt</h6>

      <Select
        value={activeTemplate?.id}
        onValueChange={handleSelectTemplate}
        disabled={isLoading}
      >
        <SelectTrigger className="w-full ring-offset-0 focus:outline-none focus:ring-0 focus:ring-transparent focus:ring-offset-0">
          <SelectValue placeholder="Select a template">
            {!activeTemplate?.id ? "New Template" : activeTemplate?.name}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="w-full">
          <SelectGroup>
            <SelectLabel className="relative -left-4 text-[#999] typography-body-5">
              Default email prompt
            </SelectLabel>
            {defaultEmailTemplates?.map((template) => (
              <SelectItem
                key={template.id}
                value={template.id as string}
                className="w-full cursor-pointer"
              >
                <span
                  className={clsxMerge(
                    "flex items-center text-left",
                    activeTemplate?.id === template.id && "font-semibold"
                  )}
                >
                  <LayoutPanelTopIcon className="mr-2 w-4 min-w-4" />
                  {template.name}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>

          {!!customEmailTemplates?.length && (
            <SelectGroup>
              <SelectLabel className="relative -left-4 text-[#999] typography-body-5">
                Custom email prompt
              </SelectLabel>
              {customEmailTemplates?.map((template) => (
                <SelectItem
                  key={template.id as string}
                  value={template.id as string}
                  className={clsxMerge(
                    "block w-full",
                    "[&>span:first-child]:top-3",
                    "cursor-pointer",
                    activeTemplate?.id === template.id && "font-semibold"
                  )}
                  onMouseDown={handleSelectCustomTemplate(
                    template.id as string
                  )}
                >
                  <span
                    className={clsxMerge(
                      "flex items-center justify-between",
                      activeTemplate?.id === template.id && "font-semibold"
                    )}
                  >
                    <span className="flex min-w-0 items-center">
                      <LayoutPanelTopIcon className="mr-2 w-4 min-w-4" />
                      <span className="max-w-[250px] truncate">
                        {template.name}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center">
                      <span className="p-1">
                        <XIcon
                          className="w-4 min-w-4"
                          data-custom-template-action-type={"delete"}
                        />
                      </span>
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
          )}

          <hr className="my-1 w-full bg-[#F4F4F5]" />

          <SelectItem
            key={ADD_PROMPT_SELECT_ITEM_KEY}
            value={ADD_PROMPT_SELECT_ITEM_KEY}
          >
            <span className="flex cursor-pointer text-left">
              <PlusIcon className="mr-2 w-4 min-w-4" />
              Add prompt
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
